import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import NewCreatingPositionLoader from 'src/components/position/NewCreatingPositionLoader';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { useGetPositionByIdQuery } from 'src/hooks/position.hooks';
import { RecruiterFormData, WorkingMode } from 'types';
import DriveFormForRecruiter from '../newRecruiterFlow/DriveFormForRecruiter';
import { updateDrivePosition } from 'src/apis/drive';

function EditDrivePositionRouter() {
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { positionId, companyId } = useParams();
	const { isAccountManagerLoggedIn } = useUserContext();
	const { isLoading, data: positionData } = useGetPositionByIdQuery(positionId);

	const [formData, setFormData] = useState<RecruiterFormData>();

	const UpdateDrivePositionMutation = useMutation(
		({ id, data }: { id: string; data: any }) => updateDrivePosition(id, data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries('getDrivePositions');
				queryClient.refetchQueries('getDrivePositions');
				toast({
					title: 'Position Updated Successfully.',
					type: 'success',
					duration: 3000,
				});
				navigate(isAccountManagerLoggedIn ? `/drive-positions/${companyId}` : '/drive-positions');
			},
			onError: (error: Error) => {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: errorMessage,
					type: 'error',
					duration: 3000,
				});
			},
		},
	);

	useEffect(() => {
		if (positionData) {
			setFormData({
				positionTitles: positionData?.position?.title ? [positionData?.position?.title] : [],
				salary: {
					min: positionData?.position?.salary?.min ?? 0,
					max: positionData?.position?.salary?.max ?? 80,
				},
				techStacks:
					positionData?.position?.positionRequirement?.mustHaveskills?.map(skill => skill as unknown as string) || [],
				company: positionData?.position?.company || undefined,
				...(positionData?.position?.positionLabel ? { positionLabel: positionData.position.positionLabel } : {}),
				...(positionData?.position?.company ? { company: positionData.position.company } : {}),
				...(positionData?.position?.vacancy ? { openings: positionData.position.vacancy } : {}),
				...(positionData?.position?.numberOfRounds ? { rounds: positionData.position.numberOfRounds } : {}),
				...(positionData?.position?.workingMode
					? { workingMode: positionData.position.workingMode as WorkingMode }
					: {}),
				...(positionData?.position?.peopleJoiningForDrive
					? { peopleJoining: positionData.position.peopleJoiningForDrive }
					: {}),
				...(positionData?.position?.workLocations ? { companyLocation: positionData.position.workLocations } : []),
			});
		}
	}, [positionData]);

	if (isLoading)
		return (
			<div className="mt-6">
				<NewCreatingPositionLoader showAnimation={true} progressBar={false} customClassCss="!w-[30%] mx-auto" />
			</div>
		);

	return (
		<div className="md:py-6">
			<div className="max-w-[580px] mx-auto dark:bg-[#111E2C] bg-[#fff] p-4 md:p-6 md:rounded-lg border dark:border-0">
				<h1 className="text-xl md:text-2xl font-bold dark:text-white text-[#28293D] mb-2">Edit Drive Position</h1>
				<DriveFormForRecruiter
					onSubmit={(UpdatedformData: RecruiterFormData) => {
						UpdateDrivePositionMutation.mutate({ id: positionId!, data: UpdatedformData });
					}}
					existingPositionData={formData}
				/>
			</div>
		</div>
	);
}

export default EditDrivePositionRouter;
